import { ReactNode } from 'react';
import { LOCATION_CHANGE } from 'redux-first-history';
import { createAppSlice } from '.';
import { EquipmentModel } from '../constants/equipmentConstants';
import { PARCELS_TOGGLE_TYPE, initialParcelsFilters } from '../constants/filtersConstants';
import { I18n, LocaleKey } from '../i18n';
import { formatPageTitle } from '../utils';
import { localStorageKeys } from '../utils/storage';
import { Values } from '../utils/types';
import { ClusterId } from './clusters';
import { CropId } from './crops';
import { EquipmentId } from './equipments';
import { Parcel, ParcelId } from './parcels';
import { EquipmentSessionId } from './sessions';
import { TagId } from './tags';

export type ParcelType = {
  id: string;
  i18nLabel: string;
  type: Values<typeof PARCELS_TOGGLE_TYPE>;
  label: string;
  checkingFunc: (parcel: Parcel) => boolean;
  icon: ReactNode;
};

export type CropSelection = {
  id: CropId;
  label: string;
};

export type TagItem = {
  id: TagId;
  label?: string;
  color?: string;
};

type AreaRange =
  | {
      start: number;
      end: number;
    }
  | {
      start?: undefined;
      end?: undefined;
    };

type State = {
  fullScreenMap: boolean;
  fullScreenDashboardMap: boolean;
  showMapSessionReplay: boolean;
  searchFilter: string;
  mapFitRequestId: number;
  selectedClusters: ClusterId[];
  parcelSessionsTracksToggle: boolean;
  equipmentSessionsTracksToggle: boolean;
  parcelsBoxesOffset: number;
  equipmentsBoxesOffset: number;
  equipmentPageOffset: Record<EquipmentId, number | undefined>;
  parcelPageOffset: Record<ParcelId, number | undefined>;
  workedParcelsPageOffset: Record<EquipmentSessionId, number | undefined>;
  useIgnTiles: boolean;
  selectedParcelTypes: ParcelType[];
  selectedCrops: CropSelection[];
  selectedTags: TagItem[];
  initialTags: TagId[];
  hasFetchedInitialTags: boolean;
  selectedAreaRange: AreaRange;
  selectedParcelIds: ParcelId[];
  parcelEquipmentModelFilters: EquipmentModel[];
  hideIntercomMessenger: boolean;
  title: string;
  selectedWorksiteReportEquipmentModel: EquipmentModel | null;
  selectedWorksiteReportCluster: ClusterId | null;
  showObservationsOnMap: boolean;
};

const initialState: State = {
  fullScreenMap: false,
  fullScreenDashboardMap: false,
  showMapSessionReplay: false,
  searchFilter: '',
  mapFitRequestId: 0,
  selectedClusters: [],
  parcelSessionsTracksToggle: false,
  equipmentSessionsTracksToggle: false,
  parcelsBoxesOffset: 0,
  equipmentsBoxesOffset: 0,
  equipmentPageOffset: {},
  parcelPageOffset: {},
  workedParcelsPageOffset: {},
  useIgnTiles: localStorage.getItem(localStorageKeys.ign) === 'true',
  selectedParcelTypes: initialParcelsFilters.parcelTypesCheckboxes.items
    .map((checkbox) => ({
      ...checkbox,
      i18nLabel: I18n.t(checkbox.label as LocaleKey),
      id: checkbox.label,
    }))
    .filter((t) => t.id !== 'Filters.inactive_parcel'),
  selectedCrops: [],
  selectedTags: [],
  initialTags: [],
  hasFetchedInitialTags: false,
  selectedAreaRange: {},
  selectedParcelIds: [],
  parcelEquipmentModelFilters: [],
  hideIntercomMessenger: false,
  title: formatPageTitle(),
  selectedWorksiteReportEquipmentModel: null,
  selectedWorksiteReportCluster: null,
  showObservationsOnMap: true,
};

const uiSlice = createAppSlice({
  name: 'ui',
  initialState,
  reducers: (create) => ({
    selectClusters: create.reducer<ClusterId[]>((state, { payload }) => {
      for (const clusterId of payload) {
        if (state.selectedClusters.includes(clusterId)) {
          state.selectedClusters = state.selectedClusters.filter((id) => id !== clusterId);
        } else {
          state.selectedClusters.push(clusterId);
        }
      }
      state.mapFitRequestId += 1;
    }),
    clearClusters: create.reducer((state) => {
      state.selectedClusters = initialState.selectedClusters;
    }),
    updateSearch: create.reducer<string>((state, { payload }) => {
      state.searchFilter = payload;
    }),
    mapFitRequested: create.reducer((state) => {
      state.mapFitRequestId += 1;
    }),
    updateTitle: create.reducer<string | undefined>((state, { payload }) => {
      state.title = formatPageTitle(payload);
    }),
    toggleFullScreen: create.reducer((state) => {
      state.fullScreenMap = !state.fullScreenMap;
    }),
    toggleDashboardFullScreen: create.reducer((state) => {
      state.fullScreenDashboardMap = !state.fullScreenDashboardMap;
    }),
    setShowSessionReplay: create.reducer<boolean>((state, { payload }) => {
      state.fullScreenMap = payload;
      state.showMapSessionReplay = payload;
    }),
    toggleParcelSessionTracks: create.reducer((state) => {
      state.parcelSessionsTracksToggle = !state.parcelSessionsTracksToggle;
    }),
    toggleEquipmentSessionTracks: create.reducer((state) => {
      state.equipmentSessionsTracksToggle = !state.equipmentSessionsTracksToggle;
    }),
    setParcelBoxesOffset: create.reducer<number>((state, { payload }) => {
      state.parcelsBoxesOffset = payload;
    }),
    setEquipmentBoxesOffset: create.reducer<number>((state, { payload }) => {
      state.equipmentsBoxesOffset = payload;
    }),
    setEquipmentPageOffset: create.reducer<{ id: EquipmentId; value?: number }>((state, { payload }) => {
      state.equipmentPageOffset[payload.id] = payload.value;
    }),
    setParcelPageOffset: create.reducer<{ id: ParcelId; value?: number }>((state, { payload }) => {
      state.parcelPageOffset[payload.id] = payload.value;
    }),
    setWorkedParcelsPageOffset: create.reducer<{ id: EquipmentSessionId; value?: number }>((state, { payload }) => {
      state.workedParcelsPageOffset[payload.id] = payload.value;
    }),
    toggleIgnTiles: create.reducer((state) => {
      localStorage.setItem(localStorageKeys.ign, `${!state.useIgnTiles}`);
      state.useIgnTiles = !state.useIgnTiles;
    }),
    toggleParcelTypes: create.reducer<ParcelType[]>((state, { payload }) => {
      const parcelTypeLabels = state.selectedParcelTypes.map((t) => t.label);
      for (const parcelType of payload) {
        if (parcelTypeLabels.includes(parcelType.label)) {
          state.selectedParcelTypes = state.selectedParcelTypes.filter((t) => t.label !== parcelType.label);
        } else {
          state.selectedParcelTypes.push(parcelType);
        }
      }
    }),
    setParcelTypes: create.reducer<ParcelType[]>((state, { payload }) => {
      state.selectedParcelTypes = payload;
    }),
    clearParcelTypes: create.reducer((state) => {
      state.selectedParcelTypes = initialState.selectedParcelTypes;
    }),
    toggleCrops: create.reducer<CropSelection[]>((state, { payload }) => {
      const cropIds = state.selectedCrops.map((t) => t.id);
      for (const crop of payload) {
        if (cropIds.includes(crop.id)) {
          state.selectedCrops = state.selectedCrops.filter((t) => t.id !== crop.id);
        } else {
          state.selectedCrops.push(crop);
        }
      }
    }),
    setCrops: create.reducer<CropSelection[]>((state, { payload }) => {
      state.selectedCrops = payload;
    }),
    clearCrops: create.reducer((state) => {
      state.selectedCrops = initialState.selectedCrops;
    }),
    setAreaRange: create.reducer<AreaRange>((state, { payload }) => {
      state.selectedAreaRange = payload;
    }),
    clearAreaRange: create.reducer((state) => {
      state.selectedAreaRange = initialState.selectedAreaRange;
    }),
    setTags: create.reducer<TagItem[]>((state, { payload }) => {
      state.selectedTags = payload;
    }),
    toggleTags: create.reducer<TagItem[]>((state, { payload }) => {
      const tagIds = state.selectedTags.map((t) => t.id);
      for (const tag of payload) {
        if (tagIds.includes(tag.id)) {
          state.selectedTags = state.selectedTags.filter((t) => t.id !== tag.id);
        } else {
          state.selectedTags.push(tag);
        }
      }
    }),
    setDefaultTags: create.reducer<TagId[]>((state, { payload }) => {
      state.initialTags = payload;
      state.hasFetchedInitialTags = true;
    }),
    clearTags: create.reducer((state) => {
      state.selectedTags = state.initialTags.map((id) => ({ id }));
    }),
    setInitialTags: create.asyncThunk((tagIds: TagId[], { dispatch }) => {
      dispatch(uiSlice.actions.setTags(tagIds.map((id) => ({ id }))));
      dispatch(uiSlice.actions.setDefaultTags(tagIds));
    }),
    toggleParcelIds: create.reducer<ParcelId[]>((state, { payload }) => {
      const parcelIds = state.selectedParcelIds;
      for (const parcelId of payload) {
        if (parcelIds.includes(parcelId)) {
          state.selectedParcelIds = parcelIds.filter((id) => id !== parcelId);
        } else {
          state.selectedParcelIds.push(parcelId);
        }
      }
    }),
    setParcelIds: create.reducer<ParcelId[]>((state, { payload }) => {
      state.selectedParcelIds = payload;
    }),
    clearParcelIds: create.reducer((state) => {
      state.selectedParcelIds = initialState.selectedParcelIds;
    }),
    parcelEquipmentModelFiltersChanged: create.reducer<EquipmentModel[]>((state, { payload }) => {
      state.parcelEquipmentModelFilters = payload;
    }),
    setIntercomMessengerHidden: create.reducer<boolean>((state, { payload }) => {
      state.hideIntercomMessenger = payload;
    }),
    setWorksiteReportEquipmentModel: create.reducer<EquipmentModel>((state, { payload }) => {
      state.selectedWorksiteReportEquipmentModel = payload;
    }),
    setWorksiteReportCluster: create.reducer<ClusterId>((state, { payload }) => {
      state.selectedWorksiteReportCluster = payload;
    }),
    setShowObservationsOnMap: create.reducer<boolean>((state, { payload }) => {
      state.showObservationsOnMap = payload;
    }),
    resetParcelFilters: create.asyncThunk((_, { dispatch }) => {
      dispatch(uiSlice.actions.clearClusters());
      dispatch(uiSlice.actions.clearParcelTypes());
      dispatch(uiSlice.actions.clearCrops());
      dispatch(uiSlice.actions.clearAreaRange());
      dispatch(uiSlice.actions.clearTags());
      dispatch(uiSlice.actions.parcelEquipmentModelFiltersChanged([]));
    }),
  }),
  extraReducers: (builder) => {
    builder.addCase(LOCATION_CHANGE, (state) => {
      state.searchFilter = '';
    });
  },
  selectors: {
    selectUIValues: (state) => state,
    selectMapFitRequestId: (state) => state.mapFitRequestId,
    selectWorksiteReportEquipmentModel: (state) => state.selectedWorksiteReportEquipmentModel,
    selectWorksiteReportCluster: (state) => state.selectedWorksiteReportCluster,
    selectShowObservationsOnMap: (state) => state.showObservationsOnMap,
    selectTitle: (state) => state.title,
    selectSelectedClusters: (state) => state.selectedClusters,
    selectSearchFilter: (state) => state.searchFilter,
    selectFullScreenMap: (state) => state.fullScreenMap,
    selectFullScreenDashboardMap: (state) => state.fullScreenDashboardMap,
    selectShowMapSessionReplay: (state) => state.showMapSessionReplay,
    selectParcelSessionsTracksToggle: (state) => state.parcelSessionsTracksToggle,
    selectEquipmentSessionsTracksToggle: (state) => state.equipmentSessionsTracksToggle,
    selectEquipmentsBoxesOffset: (state) => state.equipmentsBoxesOffset,
    selectEquipmentPageOffset: (state) => state.equipmentPageOffset,
    selectSelectedCrops: (state) => state.selectedCrops,
    selectSelectedParcelTypes: (state) => state.selectedParcelTypes,
    selectSelectedTags: (state) => state.selectedTags,
    selectSelectedAreaRange: (state) => state.selectedAreaRange,
    selectSelectedParcelIds: (state) => state.selectedParcelIds,
    selectParcelEquipmentModelFilters: (state) => state.parcelEquipmentModelFilters,
    selectParcelsBoxesOffset: (state) => state.parcelsBoxesOffset,
    selectWorkedParcelsPageOffset: (state) => state.workedParcelsPageOffset,
    selectInitialTags: (state) => state.initialTags,
    selectHasFetchedInitialTags: (state) => state.hasFetchedInitialTags,
    selectParcelPageOffset: (state) => state.parcelPageOffset,
    selectUseIgnTiles: (state) => state.useIgnTiles,
    selectHideIntercomMessenger: (state) => state.hideIntercomMessenger,
  },
});

export const {
  toggleFullScreen,
  toggleDashboardFullScreen,
  setShowSessionReplay,
  updateSearch,
  mapFitRequested,
  updateTitle,
  setParcelBoxesOffset,
  setEquipmentBoxesOffset,
  setEquipmentPageOffset,
  setParcelPageOffset,
  setWorkedParcelsPageOffset,
  toggleIgnTiles,
  toggleParcelSessionTracks,
  toggleEquipmentSessionTracks,
  selectClusters,
  clearClusters,
  toggleParcelTypes,
  setParcelTypes,
  toggleCrops,
  setCrops,
  clearCrops,
  setAreaRange,
  setTags,
  toggleTags,
  setInitialTags,
  toggleParcelIds,
  setParcelIds,
  clearParcelIds,
  setIntercomMessengerHidden,
  resetParcelFilters,
  parcelEquipmentModelFiltersChanged,
  setWorksiteReportEquipmentModel,
  setWorksiteReportCluster,
  setShowObservationsOnMap,
} = uiSlice.actions;

export const {
  selectEquipmentPageOffset,
  selectEquipmentSessionsTracksToggle,
  selectEquipmentsBoxesOffset,
  selectFullScreenDashboardMap,
  selectFullScreenMap,
  selectHasFetchedInitialTags,
  selectHideIntercomMessenger,
  selectInitialTags,
  selectMapFitRequestId,
  selectParcelEquipmentModelFilters,
  selectParcelPageOffset,
  selectParcelSessionsTracksToggle,
  selectParcelsBoxesOffset,
  selectSearchFilter,
  selectSelectedAreaRange,
  selectSelectedClusters,
  selectSelectedCrops,
  selectSelectedParcelIds,
  selectSelectedParcelTypes,
  selectSelectedTags,
  selectShowMapSessionReplay,
  selectShowObservationsOnMap,
  selectTitle,
  selectUIValues,
  selectUseIgnTiles,
  selectWorkedParcelsPageOffset,
  selectWorksiteReportCluster,
  selectWorksiteReportEquipmentModel,
} = uiSlice.selectors;

export default uiSlice.reducer;
